import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BoutiqueProvider } from './contexts/BoutiqueContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout & Common Components
import Layout from './components/common/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import AuthDebug from './components/common/AuthDebug';

// Auth Components
import LoginForm from './components/auth/LoginForm';
import ForgotPassword from './components/auth/ForgotPassword';

// Dashboard Components
import SuperAdminDashboard from './components/dashboard/SuperAdminDashboard';
import ManagerDashboard from './components/dashboard/ManagerDashboard';
import EmployeeDashboard from './components/dashboard/EmployeeDashboard';

// Super Admin Components
import BoutiquesList from './components/boutiques/BoutiquesList';
import UsersList from './components/users/UsersList';

// ✅ AJOUT : Imports pour les composants de stock et produits
import StockManagement from './components/products/StockManagement';
import ProductsList from './components/products/ProductsList';
import SaleInterface from './components/sales/SaleInterface';
import CustomersList from './components/sales/CustomersList';
import SalesList from './components/sales/SalesList';

// Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/globals.css';
import TeamManagement from './components/users/TeamManagement';
import CategoriesManagement from './components/products/CategoriesManagement';
import CreditManagement from './components/sales/CreditManagement';
import UserProfile from './components/users/UserProfil';
import ReportingPage from './components/reporting/ReportingPage';
import InvoiceManagement from './components/invoices/InvoiceManagement';
import ExpensesList from './components/sales/ExpensesList';
import ProductBulkImport from './components/products/ProductBulkImport';
import PromotionManagement from './components/products/PromotionManagement';
import ModelsManagement from './components/atelier/models/ModelsManagement';
import AtelierMeasurementsPage from './components/atelier/mesures/AtelierMeasurementsPage';
import CustomOrdersList from './components/atelier/commande/CustomOrdersList';
import AtelierDashboard from './components/atelier/AtelierDashboard';
import SessionHistory from './components/cashSession/Sessionhistory';

function App() {
  return (
    <AuthProvider>
      <BoutiqueProvider>
        <ThemeProvider>
            <Router basename="/smartboutik">
              <div className="App">
                
                
                <Routes>
                  {/* Routes publiques */}
                  <Route path="/login" element={<LoginForm />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  
                  {/* Routes protégées avec layout */}
                  <Route path="/" element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }>
                    
                    {/* Dashboard par rôle */}
                    <Route index element={<DashboardRouter />} />
                    
                    {/* Routes Super Admin */}
                    <Route path="admin" element={
                      <ProtectedRoute roles={['SUPER_ADMIN']}>
                        <SuperAdminDashboard />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="admin/boutiques" element={
                      <ProtectedRoute roles={['SUPER_ADMIN']}>
                        <BoutiquesList />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="admin/users" element={
                      <ProtectedRoute roles={['SUPER_ADMIN']}>
                        <UsersList />
                      </ProtectedRoute>
                    } />
                    
                    {/* Routes Manager */}
                    <Route path="manager" element={
                      <ProtectedRoute roles={['MANAGER']}>
                        <ManagerDashboard />
                      </ProtectedRoute>
                    } />
                    
                    {/* ✅ AJOUT : Routes pour les fonctionnalités boutique */}
                    <Route path="products" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <ProductsList />
                      </ProtectedRoute>
                    } />

                    <Route 
                      path="/products/import" 
                      element={
                        <ProtectedRoute requiredRole="MANAGER">
                          <ProductBulkImport />
                        </ProtectedRoute>
                      } 
                    />
                    
                    <Route path="stock" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <StockManagement />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="pos" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <SaleInterface />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="sales" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <SalesList />
                      </ProtectedRoute>
                    } />

                    {/* ✅ NOUVEAU : Dépenses */}
                    <Route 
                      path="expenses" 
                      element={
                        <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                          <ExpensesList />
                        </ProtectedRoute>
                      } 
                    />

                    {/* ✅ NOUVELLE ROUTE FACTURES */}
                    <Route 
                      path="invoices" 
                      element={
                        <ProtectedRoute>
                          <InvoiceManagement />
                        </ProtectedRoute>
                      } 
                    />  
                    
                    <Route path="customers" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <CustomersList />
                      </ProtectedRoute>
                    } />
                    
                    {/* Routes Employee */}
                    <Route path="employee" element={
                      <ProtectedRoute roles={['EMPLOYEE']}>
                        <EmployeeDashboard />
                      </ProtectedRoute>
                    } />

                    <Route path="categories" element={
                      <ProtectedRoute roles={['MANAGER']}>
                        <CategoriesManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="team" element={
                      <ProtectedRoute roles={['MANAGER']}>
                        <TeamManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="promos" element={
                      <ProtectedRoute roles={['MANAGER']}>
                        <PromotionManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="credits" element={
                      <ProtectedRoute roles={['MANAGER']}>
                        <CreditManagement />
                      </ProtectedRoute>
                    } />

                    {/* ✅ ATELIER - Dashboard principal */}
                    <Route path="atelier" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <AtelierDashboard />
                      </ProtectedRoute>
                    } />

                    {/* ✅ ATELIER - Commandes atelier */}
                    <Route path="atelier/orders" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <CustomOrdersList />
                      </ProtectedRoute>
                    } />

                    <Route path="profile" element={
                      <ProtectedRoute>
                        <UserProfile />
                      </ProtectedRoute>
                    } />

                    <Route path="analytics" element={
                      <ProtectedRoute>
                        <ReportingPage />
                      </ProtectedRoute>
                    } />

                    {/* ✅ ATELIER - Routes module atelier */}
                    <Route path="atelier/models" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <ModelsManagement />
                      </ProtectedRoute>
                    } />

                    {/* ✅ ATELIER - Route page Mesures */}
                    <Route path="atelier/measurements" element={
                      <ProtectedRoute roles={['MANAGER', 'EMPLOYEE']}>
                        <AtelierMeasurementsPage />
                      </ProtectedRoute>
                    } />

                    {/* ✅ SESSIONS DE CAISSE - Manager uniquement */}
                <Route path="manager/sessions" element={
                  <ProtectedRoute roles={['MANAGER', 'SUPER_ADMIN']}>
                    <SessionHistory />
                  </ProtectedRoute>
                } />
                    
                  </Route>

                  
                  
                  {/* Redirection par défaut */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                
                {/* Composant de debug (seulement en développement) */}
                {process.env.NODE_ENV === 'development' && <AuthDebug />}
              </div>
            </Router>
        </ThemeProvider>
      </BoutiqueProvider>
    </AuthProvider>
  );
}

// Composant pour router vers le bon dashboard selon le rôle
function DashboardRouter() {
  const { user } = useAuth();
  
  if (!user) return <LoadingSpinner />;
  
  switch (user.role) {
    case 'SUPER_ADMIN':
      return <Navigate to="/admin" replace />;
    case 'MANAGER':
      return <Navigate to="/manager" replace />;
    case 'EMPLOYEE':
      return <Navigate to="/employee" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
}

export default App;